import React, { Suspense, lazy, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import Favicon from 'react-favicon';
import dayjs from 'dayjs';

import DashboardLayout from 'layout/DashboardLayout';

import Loading from 'components/Loading';
import Head from 'components/Head';

import { showSnackbar, hideSnackbar } from 'modules/App/store/actions';

import { convertGSToDownloadURL } from 'utils';
import buildMatch from 'utils/buildMatch';

const ListPages = lazy(() => import('modules/Web/container/ListPages'));
const Illustrations = lazy(() =>
  import('modules/Illustration/container/Illustrations'),
);

const ProjectsPage = ({
  projects,
  currentProject,
  dispatch,
  teams,
  user,
  matchpath,
}) => {
  const location = useLocation();
  const params = useParams();
  const match = buildMatch(location, params, matchpath);

  const {
    params: { projectID },
  } = match;

  const [favicon, setFavicon] = useState('/favicon.ico');

  const type = () => {
    const thisProject = projects.find((p) => p.id === projectID);

    if (
      thisProject?.isExpired === true ||
      (typeof thisProject.isExpired === 'object'
        ? dayjs(thisProject.isExpired.toDate()).isBefore(dayjs())
        : false)
    )
      return null;

    if (currentProject.id !== projectID) return thisProject.type || null;

    if (currentProject.id && currentProject.id === projectID)
      return currentProject.type;

    return null;
  };

  const showNotFoundMessage = () => {
    setTimeout(() => {
      dispatch(
        showSnackbar(
          'Project is not found or accessible. Redirecting to all projects.',
        ),
      );
      setTimeout(() => dispatch(hideSnackbar()), 5000);
    }, 50);

    return <></>;
  };

  useEffect(() => {
    const team = (() => {
      if (teams && teams.length) {
        return teams?.find((t) => t.owner === user.uid) || teams[0];
      }

      return null;
    })();

    if (team) {
      const { isDisabled, customBranding, theme } = team;

      if (isDisabled === false && customBranding && theme.iconURL) {
        if (theme.iconURL.startsWith('gs://')) {
          setFavicon(convertGSToDownloadURL(theme.iconURL, false));
        }
      } else {
        setFavicon('/favicon.ico');
      }
    } else {
      setFavicon('/favicon.ico');
    }
  }, [teams, user]);

  return (
    <>
      {currentProject.title && (
        <Head
          title={`${currentProject.title.substring(0, 20)}${
            currentProject.title.length > 20 ? '...' : ''
          }`}
        />
      )}
      <Favicon url={favicon} />
      <DashboardLayout match={match}>
        {projects === null ? (
          <Loading />
        ) : (
          <>
            {type() ? (
              <>
                {(type().startsWith('web') || type() === 'demo') && (
                  <Suspense fallback={<Loading />}>
                    <ListPages projectID={projectID} match={match} />
                  </Suspense>
                )}
                {type() === 'illustration' && (
                  <Suspense fallback={<Loading />}>
                    <Illustrations />
                  </Suspense>
                )}
              </>
            ) : (
              <>
                {showNotFoundMessage()}
                <Navigate to="/projects" replace />
              </>
            )}
          </>
        )}
      </DashboardLayout>
    </>
  );
};

const mapStateToProps = ({
  project: { projects, currentProject },
  user: { user },
  team: { teams },
}) => ({
  projects,
  currentProject,
  user,
  teams,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

ProjectsPage.propTypes = {
  projects: PropTypes.array,
  currentProject: PropTypes.object,
  dispatch: PropTypes.func,
  user: PropTypes.object,
  teams: PropTypes.array,
  matchpath: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectsPage);
